import type { PriceListEntry } from "./buildPriceListEntry"
import { getLayoutCurrencyCodes } from "./layoutPricingCurrency"

export type ProductPriceList = Record<string, PriceListEntry>

/** Build a price list from Contentful manual pricing fields for the layout's currencyList. */
export function buildContentfulPriceList(
  item: unknown,
  currencyList?: unknown,
): ProductPriceList {
  if (!item || typeof item !== "object") return {}

  const card = item as Record<string, unknown>
  const priceList: ProductPriceList = {}

  for (const code of getLayoutCurrencyCodes(currencyList)) {
    const lower = code.toLowerCase()
    const price = card[code === "USD" ? "pricingValue" : `${lower}Price`]
    if (typeof price !== "string" || !price.trim()) continue

    const crossedOut = card[`${lower}CrossedOutPrice`]
    priceList[code] = {
      currency: price,
      crossedOutPrice: typeof crossedOut === "string" ? crossedOut : "",
      discount: typeof card.discount === "string" ? card.discount : null,
      discountCode:
        typeof card.discountCode === "string" ? card.discountCode : null,
    }
  }

  return priceList
}

/** Contentful manual prices win per currency; API fills currencies without an override. */
export function resolveCardPriceList(
  contentfulPrices: ProductPriceList,
  apiPrices: ProductPriceList,
): ProductPriceList {
  const resolved: ProductPriceList = {}

  for (const [code, entry] of Object.entries(apiPrices)) {
    if (entry?.currency) resolved[code] = entry
  }

  for (const [code, entry] of Object.entries(contentfulPrices)) {
    if (entry?.currency) resolved[code] = entry
  }

  return resolved
}

/** Currency codes in the layout list that have no manual Contentful price on this card. */
export function getCurrencyCodesNeedingApiPrice(
  item: unknown,
  currencyList?: unknown,
): string[] {
  const manualPrices = buildContentfulPriceList(item, currencyList)

  return getLayoutCurrencyCodes(currencyList).filter(
    (code) => !manualPrices[code]?.currency,
  )
}
